import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Card, Button, Spinner, PageHeader } from '../components/common';
import SignaturePad from '../components/common/SignaturePad';
import { formatDataOra } from '../utils';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const semnaturaAPI = {
  getCerere:       (id)  => api.get(`/cereri/${id}`).then(r => r.data.data),
  getSemnaturi:    (id)  => api.get(`/semnaturi/cerere/${id}`).then(r => r.data.data),
  aplicaPad:       (d)   => api.post('/semnaturi/pad', d),
  initiazaEIDAS:   (d)   => api.post('/semnaturi/eidas/initiate', d),
  verificaSemnatura:(id) => api.get(`/semnaturi/${id}/verifica`).then(r => r.data.data),
  getDocumente:    (id)  => api.get(`/flux/${id}`).then(r => r.data.data?.documente || []),
};

export default function SemnaturePage() {
  const { cerereId }  = useParams();
  const navigate      = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [tipActiv,    setTipActiv]    = useState('pad');
  const [documentId,  setDocumentId]  = useState('');
  const [showPad,     setShowPad]     = useState(false);
  const [documentHash, setDocumentHash] = useState('');

  const { data: cerere,    isLoading: l1 } = useQuery(['cerere',    cerereId], () => semnaturaAPI.getCerere(cerereId));
  const { data: semnaturi, isLoading: l2, refetch } = useQuery(['semnaturi', cerereId], () => semnaturaAPI.getSemnaturi(cerereId));
  const { data: documente, isLoading: l3 } = useQuery(['flux-doc',  cerereId], () => semnaturaAPI.getDocumente(cerereId));

  const mutPad = useMutation(
    (d) => semnaturaAPI.aplicaPad(d),
    {
      onSuccess: (r) => {
        toast.success('Semnătură aplicată cu succes!');
        setShowPad(false);
        refetch();
        if (r.data.downloadUrl) window.open(r.data.downloadUrl, '_blank');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Eroare aplicare semnătură'),
    }
  );

  const mutEIDAS = useMutation(
    (d) => semnaturaAPI.initiazaEIDAS(d),
    {
      onSuccess: (r) => {
        toast.info('Redirecționare către certSIGN...');
        window.location.href = r.data.redirectUrl;
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Eroare inițiere eIDAS'),
    }
  );

  if (l1 || l2 || l3) return <Layout><Spinner message="Se încarcă..." /></Layout>;

  // Calculăm hash-ul documentului selectat (în practică vine de la backend)
  const documentSelectat = (documente || []).find(d => d.id === documentId);

  function handleSalvarePad(svgData, biometricData) {
    if (!documentId) { toast.warning('Selectați mai întâi un document'); return; }
    mutPad.mutate({
      cerereId,
      documentId,
      svgData,
      biometricData,
      documentHash: documentHash || 'hash-placeholder-' + Date.now(),
    });
  }

  function handleEIDAS() {
    if (!documentId) { toast.warning('Selectați mai întâi un document'); return; }
    mutEIDAS.mutate({ cerereId, documentId });
  }

  return (
    <Layout>
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate(`/cereri/${cerereId}`)} style={s.back}>← Înapoi la cerere</button>
      </div>

      <PageHeader
        title="Semnare document"
        subtitle={`Cererea ${cerere?.numar_cerere || ''}`}
      />

      <div style={{ ...s.grid2, gridTemplateColumns: isMobile ? '1fr' : '1fr 340px' }}>
        {/* Coloana stânga — semnare */}
        <div>
          {/* Selectare document */}
          <Card title="1. Selectează documentul de semnat">
            {(!documente || documente.length === 0)
              ? <p style={s.gol}>Nu există documente generate pentru această cerere. Generați un document din pagina de flux.</p>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {documente.map(doc => (
                    <label key={doc.id} style={{
                      ...s.docOption,
                      ...(documentId === doc.id ? s.docOptionActiv : {})
                    }}>
                      <input
                        type="radio"
                        name="document"
                        value={doc.id}
                        checked={documentId === doc.id}
                        onChange={() => setDocumentId(doc.id)}
                      />
                      <div style={s.docInfo}>
                        <span style={s.docTitlu}>{doc.titlu}</span>
                        <span style={s.docMeta}>{doc.tip} · {doc.status} · {formatDataOra(doc.created_at)}</span>
                      </div>
                      {doc.status === 'semnat' && <span style={s.semnatBadge}>✓ Semnat</span>}
                    </label>
                  ))}
                </div>
              )
            }
          </Card>

          {/* Selectare tip semnătură */}
          <Card title="2. Alege tipul de semnătură">
            <div style={{ ...s.tipuri, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              <button
                onClick={() => setTipActiv('pad')}
                style={{ ...s.tipBtn, ...(tipActiv === 'pad' ? s.tipBtnActiv : {}) }}
              >
                <span style={s.tipIcon}>🖊</span>
                <strong>Pad fizic</strong>
                <span style={s.tipDesc}>Semnătură pe tabletă Wacom la ghișeu</span>
                <span style={s.tipLegal}>Semnătură electronică simplă</span>
              </button>

              <button
                onClick={() => setTipActiv('eidas')}
                style={{ ...s.tipBtn, ...(tipActiv === 'eidas' ? s.tipBtnActiv : {}) }}
              >
                <span style={s.tipIcon}>🔐</span>
                <strong>eIDAS calificată</strong>
                <span style={s.tipDesc}>Certificat digital certSIGN / DigiSign</span>
                <span style={s.tipLegal}>Semnătură electronică calificată</span>
              </button>
            </div>
          </Card>

          {/* Acțiune semnare */}
          <Card title="3. Aplică semnătura">
            {tipActiv === 'pad' && !showPad && (
              <div>
                <p style={s.info}>
                  Semnătura se aplică vizual pe document și include datele semnatarului,
                  data și ora, plus un hash SHA-256 pentru verificarea integrității.
                </p>
                <Button
                  onClick={() => { if (!documentId) { toast.warning('Selectați un document'); return; } setShowPad(true); }}
                  disabled={!documentId}
                >
                  🖊 Deschide pad semnătură
                </Button>
              </div>
            )}

            {tipActiv === 'pad' && showPad && (
              <SignaturePad
                width={Math.min(480, w - 48)}
                height={180}
                label="Semnați în spațiul de mai jos"
                onSave={handleSalvarePad}
                onCancel={() => setShowPad(false)}
              />
            )}

            {tipActiv === 'eidas' && (
              <div>
                <p style={s.info}>
                  Veți fi redirecționat către platforma certSIGN pentru a semna cu
                  certificatul dvs. digital calificat. Asigurați-vă că token-ul USB
                  este conectat înainte de a continua.
                </p>
                <div style={s.eidásReq}>
                  <strong>Cerințe:</strong>
                  <ul style={{ margin: '8px 0 0 16px', fontSize: '13px', color: '#555' }}>
                    <li>Certificat digital calificat emis de certSIGN sau DigiSign</li>
                    <li>Token USB conectat și driver instalat</li>
                    <li>Aplicația certSIGN instalată pe calculator</li>
                  </ul>
                </div>
                <Button
                  onClick={handleEIDAS}
                  disabled={!documentId || mutEIDAS.isLoading}
                  style={{ marginTop: '16px' }}
                >
                  {mutEIDAS.isLoading ? 'Se inițiază...' : '🔐 Semnează cu eIDAS'}
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Coloana dreapta — semnături aplicate */}
        <div>
          <Card title={`Semnături aplicate (${semnaturi?.length || 0})`}>
            {!semnaturi || semnaturi.length === 0
              ? <p style={s.gol}>Nicio semnătură aplicată încă</p>
              : semnaturi.map(s2 => (
                <div key={s2.id} style={s.semnRow}>
                  <div style={s.semnIcon}>
                    {s2.tip === 'pad' ? '🖊' : '🔐'}
                  </div>
                  <div style={s.semnInfo}>
                    <span style={s.semnNume}>{s2.semnatar_nume}</span>
                    <span style={s.semnFunctie}>{s2.semnatar_functie}</span>
                    <span style={s.semnTip}>
                      {s2.tip === 'pad' ? 'Semnătură electronică simplă' : 'Semnătură calificată eIDAS'}
                    </span>
                    {s2.certificat_cn && <span style={s.semnCert}>Cert: {s2.certificat_cn}</span>}
                    <span style={s.semnData}>{formatDataOra(s2.created_at)}</span>
                  </div>
                  <div style={{ ...s.semnValid, color: s2.valid ? '#059669' : '#dc2626' }}>
                    {s2.valid ? '✓ Validă' : '✗ Invalidă'}
                  </div>
                </div>
              ))
            }
          </Card>

          {/* Informații legale */}
          <Card title="Valabilitate legală">
            <div style={s.legal}>
              <div style={s.legalItem}>
                <span style={s.legalIcon}>🖊</span>
                <div>
                  <strong style={{ fontSize: '13px' }}>Pad fizic</strong>
                  <p style={s.legalText}>Semnătură electronică simplă conform Legii 455/2001. Valabilă pentru documente interne, confirmări de primire, procese verbale.</p>
                </div>
              </div>
              <div style={s.legalItem}>
                <span style={s.legalIcon}>🔐</span>
                <div>
                  <strong style={{ fontSize: '13px' }}>eIDAS calificată</strong>
                  <p style={s.legalText}>Semnătură electronică calificată conform Regulamentului eIDAS (UE 910/2014). Valoare juridică egală cu semnătura olografă.</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

const s = {
  back:         { background: 'none', border: 'none', cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', padding: 0 },
  grid2:        { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' },
  gol:          { color: '#888', fontSize: '14px', textAlign: 'center', padding: '24px 0', margin: 0 },
  docOption:    { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', transition: 'all .15s' },
  docOptionActiv: { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' },
  docInfo:      { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  docTitlu:     { fontSize: '14px', fontWeight: '500', color: '#1a1a2e' },
  docMeta:      { fontSize: '12px', color: '#9ca3af' },
  semnatBadge:  { fontSize: '12px', color: '#059669', fontWeight: '600' },
  tipuri:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  tipBtn:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '16px 12px', border: '2px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', backgroundColor: '#fff', transition: 'all .15s', textAlign: 'center' },
  tipBtnActiv:  { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' },
  tipIcon:      { fontSize: '28px', marginBottom: '4px' },
  tipDesc:      { fontSize: '12px', color: '#6b7280' },
  tipLegal:     { fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' },
  info:         { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '16px' },
  eidásReq:     { padding: '12px', backgroundColor: '#fef9c3', borderRadius: '8px', fontSize: '13px', color: '#713f12' },
  semnRow:      { display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' },
  semnIcon:     { fontSize: '24px', flexShrink: 0 },
  semnInfo:     { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  semnNume:     { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  semnFunctie:  { fontSize: '12px', color: '#6b7280' },
  semnTip:      { fontSize: '12px', color: '#2d2d7e', fontStyle: 'italic' },
  semnCert:     { fontSize: '11px', color: '#9ca3af' },
  semnData:     { fontSize: '11px', color: '#9ca3af' },
  semnValid:    { fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  legal:        { display: 'flex', flexDirection: 'column', gap: '16px' },
  legalItem:    { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  legalIcon:    { fontSize: '24px', flexShrink: 0 },
  legalText:    { margin: '4px 0 0', fontSize: '12px', color: '#6b7280', lineHeight: '1.5' },
};
